import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  ChangeDetectorRef,
  TemplateRef,
  ViewChild,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { combineLatest, finalize, Subject, take, takeUntil } from 'rxjs';
import { ConfirmDialogComponent } from '../../../../../components/confirm-dialog/confirm-dialog.component';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { NoDataComponent } from '../../../../../pages/content/no-data/no-data.component';
import { FbaAssessmentComponent } from '../assessment-fba/fba-assessment/fba-assessment.component';
import { InsightsDataComponent } from '../../insights-data/insights-data.component';
import { CommonService } from '../../../../../shared/services/common.service';
import { InsightMode, BehaviorStatus } from '../../../insights.enums';
import { LoaderService } from '../../../../../core/services/loader.service';
import {
  BehaviorFunction,
  CaseBehaviour,
  CaseDetailModel,
  ManageStudentDrawerParams,
} from '../../../insights.interface';
import { DefineBehaviorComponent } from '../define-behavior/define-behavior.component';
import { BehaviorService } from '../define-behavior/define-behavior.service';
import { InsightsService } from '../../../insights.service';
import { ScreenerResultsGraphComponent } from '../assessment-fba/screener-results-graph/screener-results-graph.component';
import {
  NzBreadCrumbComponent,
  NzBreadCrumbItemComponent,
} from 'ng-zorro-antd/breadcrumb';
import { ManageBehaviorStrategiesComponent } from '../manage-behavior-strategies/manage-behavior-strategies.component';
import { FunctionalAssessmentModelAnswer } from '../assessment-fba/assesment-fba.interface';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { LabelKey } from '../../../../../core/models/report-labels.model';
import { ReportLabelPipe } from '../../../../../core/pipes/report-label.pipe';
import { ReportLabelsService } from '../../../../manage-settings/report-labels/report-labels.service';
import { FidelityCheckCorrectionNeededComponent } from '../../../../administration-fidelity-check/fidelity-check-drawer/fidelity-check-correction-needed/fidelity-check-correction-needed.component';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'app-behavior-list',
  standalone: true,
  imports: [
    NzDescriptionsModule,
    NzButtonModule,
    SvgIconComponent,
    ConfirmDialogComponent,
    NoDataComponent,
    CommonModule,
    NzSwitchModule,
    FormsModule,
    ScreenerResultsGraphComponent,
    NzBreadCrumbComponent,
    NzBreadCrumbItemComponent,
    NzModalModule,
    ReportLabelPipe,
    DrawerHeaderComponent,
    NzToolTipModule,
  ],
  templateUrl: './behavior-list.component.html',
  styleUrl: './behavior-list.component.scss',
})
export class BehaviorListComponent implements OnDestroy, OnInit {
  @Input() caseDetail = {} as CaseDetailModel;
  @ViewChild('drawerTitleTpl') drawerTitleTpl!: TemplateRef<void>;
  @ViewChild('correctionNeededTpl') correctionNeededTpl!: TemplateRef<void>;
  @ViewChild('drawerBehaviorTitleTpl')
  drawerBehaviorTitleTpl!: TemplateRef<void>;
  @ViewChild('drawerTitleBehaviorStrategies')
  drawerTitleBehaviorStrategies!: TemplateRef<void>;
  @Output() listUpdated = new EventEmitter<void>();
  isDeleteDialogOpen = false;
  isDeleting = false;
  selectedBehavior?: CaseBehaviour;
  private readonly destroy$ = new Subject<void>();
  chartImage: string | null = null;
  assessmentOldData: FunctionalAssessmentModelAnswer[] = [];
  labelEnum = {
    ASSESS_LABEL_OVERRIDE: LabelKey.ASSESS_LABEL_OVERRIDE,
    BEHAVIOR_DETAILS: LabelKey.BEHAVIOR_DETAILS,
    STRATEGIES_DETAILS: LabelKey.STRATEGIES_DETAILS,
  };

  isDefinitionModalVisible = false;
  selectedDefinition = '';

  assessLabel: string = LabelKey.BEHAVIOR_DETAILS;
  behaviorDetails: string = LabelKey.BEHAVIOR_DETAILS;
  stargyDetails: string = LabelKey.STRATEGIES_DETAILS;
  fidelityCheckLabel: string = LabelKey.FIDELITY_CHECK;

  caseParams: ManageStudentDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.CREATE,
  };

  constructor(
    private drawerService: DrawerService,
    private behaviorService: BehaviorService,
    private commonService: CommonService,
    private cd: ChangeDetectorRef,
    private insightService: InsightsService,
    private loaderService: LoaderService,
    private reportLabelsService: ReportLabelsService
  ) {}

  ngOnInit(): void {
    this.insightService.assessment$
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.assessmentOldData = res;
      });
    combineLatest({
      assessLabel: this.reportLabelsService.getLabelValue(
        LabelKey.ASSESS_LABEL_OVERRIDE
      ),
      behaviorDetails: this.reportLabelsService.getLabelValue(
        LabelKey.BEHAVIOR_DETAILS
      ),
      stargyDetails: this.reportLabelsService.getLabelValue(
        LabelKey.STRATEGIES_DETAILS
      ),
      fidelityCheckLabel: this.reportLabelsService.getLabelValue(
        LabelKey.FIDELITY_CHECK
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(labels => {
        this.assessLabel = labels.assessLabel ?? LabelKey.ASSESS_LABEL_OVERRIDE;
        this.behaviorDetails =
          labels.behaviorDetails ?? LabelKey.BEHAVIOR_DETAILS;
        this.fidelityCheckLabel =
          labels.fidelityCheckLabel ?? LabelKey.FIDELITY_CHECK;
        this.stargyDetails =
          labels.stargyDetails ?? LabelKey.STRATEGIES_DETAILS;
        this.labelEnum = {
          ASSESS_LABEL_OVERRIDE: this.assessLabel as LabelKey,
          BEHAVIOR_DETAILS: this.behaviorDetails as LabelKey,
          STRATEGIES_DETAILS: this.stargyDetails as LabelKey,
        };
      });

    this.caseDetail.caseBehaviors.forEach(behavior => {
      if (behavior.statusId === BehaviorStatus.INACTIVE) {
        behavior.isExpanded = false;
      } else {
        behavior.isExpanded = true;
      }
    });
  }

  get generateRandomId(): string {
    const randomId = Math.floor(100000 + Math.random() * 900000);
    return randomId.toString();
  }

  getCaseBehaviourSubTypes(behavior: CaseBehaviour): string[] {
    return behavior.behaviorCategoryTypes.map(c => c.behaviorTypeName) || [];
  }

  getFuncationsName(behaviorFunction: BehaviorFunction[]) {
    const result = behaviorFunction
      .map(item => item.behaviorFunctionName)
      .join(', ');
    return result;
  }
  editBehavior(behavior: CaseBehaviour, isDetailPage = false) {
    const behaviorDrawerRef = this.drawerService.openDrawer(
      DefineBehaviorComponent,
      this.drawerBehaviorTitleTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        case: this.caseDetail,
        mode: isDetailPage
          ? InsightMode.VIEW_BEHAVIOR
          : InsightMode.EDIT_BEHAVIOR,
        behaviorId: behavior.id,
      },
      DRAWER_IDS.DEFINE_BEHAVIOR
    );

    behaviorDrawerRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.listUpdated.emit();
      });
  }

  closeBehaviorDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR);
  }

  openAssessmentFbaDrawer(behavior: CaseBehaviour, isDetailPage = false): void {
    const fbaDrawerRef = this.drawerService.openDrawer(
      FbaAssessmentComponent,
      this.drawerTitleTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        caseDetail: this.caseDetail,
        behavior: behavior,
        chartImage: behavior.chartImage,
        mode: isDetailPage ? InsightMode.EDIT : InsightMode.VIEW,
      },
      DRAWER_IDS.FBA
    );
    fbaDrawerRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe(assemenetData => {
        if (assemenetData) {
          if (this.assessmentOldData && this.assessmentOldData.length > 0) {
            const payload = {
              caseBehaviorId: behavior.id,
              functionalAssessmentAnswers: this.assessmentOldData,
            };
            this.loaderService.show();
            const operation$ =
              this.insightService.submitBehaviouralAssessment(payload);
            operation$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
              next: () => {
                this.insightService.assessmentSubject.next([]);
                this.listUpdated.emit();
              },
              error: error => {
                this.loaderService.hide();
                this.handleError(error);
              },
            });
          } else {
            this.listUpdated.emit();
          }
        }
      });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  openManageBehaviorStrategiesDrawer(behavior: CaseBehaviour): void {
    this.drawerService
      .openDrawer(
        ManageBehaviorStrategiesComponent,
        this.drawerTitleBehaviorStrategies as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          case: this.caseDetail,
          behavior: behavior,
          mode: InsightMode.VIEW,
          orgId: this.caseDetail.student.organizationId,
        },
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.insightService
          .getCaseById(this.caseDetail.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.listUpdated.emit();
            },
            error: error => {
              this.handleError(error);
            },
          });
      });
  }
  openDataDrawer(behavior: CaseBehaviour): void {
    this.drawerService
      .openDrawer(
        InsightsDataComponent,
        'Data',
        1170,
        true,
        undefined,
        {
          case: this.caseDetail,
          behavior: behavior,
          mode: InsightMode.VIEW,
          orgId: this.caseDetail.student.organizationId,
          caseBehaviorId: behavior.id,
        },
        DRAWER_IDS.MANAGE_DATA
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.listUpdated.emit();
      });
  }

  closeManageBehaviorStrategiesDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES);
  }

  deleteBehavior(behavior: CaseBehaviour) {
    this.isDeleteDialogOpen = true;
    this.selectedBehavior = behavior;
  }

  handleDelete(): void {
    this.isDeleting = true;
    this.behaviorService
      .deleteBehavior(Number(this.selectedBehavior?.id))
      .pipe(
        finalize(() => (this.isDeleting = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', 'Behavior deleted successfully');
          this.selectedBehavior = undefined;
          this.isDeleteDialogOpen = false;
          this.listUpdated.emit();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  handleCancel(): void {
    this.isDeleteDialogOpen = false;
    this.selectedBehavior = undefined;
  }

  onActiveInActiveBehavior(behavior: CaseBehaviour, event: Event) {
    behavior.statusId = event ? BehaviorStatus.ACTIVE : BehaviorStatus.INACTIVE;
    this.cd.detectChanges();
    this.behaviorService
      .updateBehaviorStatus(
        behavior.id,
        event ? BehaviorStatus.ACTIVE : BehaviorStatus.INACTIVE
      )
      .pipe(
        finalize(() => (this.isDeleting = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.cd.detectChanges();
          this.commonService.notify(
            'success',
            behavior.statusId === BehaviorStatus.ACTIVE
              ? 'Behavior Activated successfully'
              : 'Behavior InActivated successfully'
          );
          this.toggleBehaviorExpand(behavior);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          behavior.statusId = event
            ? BehaviorStatus.INACTIVE
            : BehaviorStatus.ACTIVE;
          this.toggleBehaviorExpand(behavior);
          this.cd.detectChanges();
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  openCorrectionNeededDrawer(behavior: CaseBehaviour): void {
    this.drawerService.openDrawer(
      FidelityCheckCorrectionNeededComponent,
      this.correctionNeededTpl as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        type: 0,
        caseDetail: this.caseDetail,
        behavior: behavior,
      },
      DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
    );
  }
  toggleBehaviorExpand(behavior: CaseBehaviour): void {
    if (behavior.statusId === BehaviorStatus.INACTIVE) {
      behavior.isExpanded = false;
    } else {
      behavior.isExpanded = true;
    }
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeFBADrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.FBA);
  }

  closeCorrectionNeededDrawer() {
    this.drawerService.closeDrawer(
      DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
    );
  }
  onChartImageGenerated(image: string, behavior: CaseBehaviour) {
    this.cd.detectChanges();
    behavior.chartImage = image;
  }

  getDaysAgoText(date: string | Date): string {
    const givenDate = new Date(date);
    const today = new Date();
    const timeDiff = today.getTime() - givenDate.getTime();

    const daysAgo = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    return `${daysAgo} days ago`;
  }

  getDaysAgoColor(date: string | Date): string {
    if (!date) {
      return '';
    }
    const givenDate = new Date(date);
    const today = new Date();
    const timeDiff = today.getTime() - givenDate.getTime();
    const daysAgo = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (daysAgo <= 10) return '';
    if (daysAgo <= 20) return 'orange-text';
    return 'red-text';
  }

  showOperationalDefinitionModal(definition: string): void {
    this.selectedDefinition = definition;
    this.isDefinitionModalVisible = true;
  }

  handleModalClose(): void {
    this.isDefinitionModalVisible = false;
  }

  resumeProcess(label: string, behavior: CaseBehaviour): void {
    if (label === this.labelEnum.BEHAVIOR_DETAILS) {
      this.editBehavior(behavior, false);
    }
    if (label === this.labelEnum.ASSESS_LABEL_OVERRIDE) {
      this.openAssessmentFbaDrawer(behavior, true);
    }
    if (label === this.labelEnum.STRATEGIES_DETAILS) {
      this.openManageBehaviorStrategiesDrawer(behavior);
    }
  }

  getDynamicLabel(behavior: CaseBehaviour): string {
    if (behavior.draft) {
      return this.behaviorDetails;
    }
    if (
      behavior.behaviorFunction.length === 0 ||
      behavior.behaviorAssessmentGraph.length === 0
    ) {
      return this.assessLabel;
    }
    if (behavior.appliedStrategyCount === 0) {
      return this.stargyDetails;
    }
    return '';
  }
}
